import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import { Pin } from 'lucide-react';
import { useOEM } from './contexts/OEMContext';
import { useFullscreen } from './contexts/FullscreenContext';
import { POSUserContext } from './contexts/POSUserContext';
import { useAuth } from './contexts/AuthContext';
import { db } from '../firebase/config';
import { isTauriMode } from '../utils/environmentDetector';
import { readHideTopBar, WINDOW_CHROME_EVENT } from '../utils/desktop/windowChrome';

async function fitRestoredWindow() {
  const { invoke } = await import('@tauri-apps/api/core');
  await invoke('fit_restored_window_command');
}

const CustomTitleBar: React.FC = () => {
  const [isMaximized, setIsMaximized] = useState(false);
  const [appName, setAppName] = useState('NuagePOS Eagle');
  const [appVersion, setAppVersion] = useState('6.6.1');
  const [currentUrl, setCurrentUrl] = useState('');
  const [showAddressBar, setShowAddressBar] = useState(false);
  const [isTauri, setIsTauri] = useState(false);
  const [isPinned, setIsPinned] = useState(false);
  const { getBrandName, loading: oemLoading } = useOEM();
  const { isFullscreen } = useFullscreen();
  const [hideTopBar, setHideTopBar] = useState(readHideTopBar);
  const { currentUser } = useAuth();
  const location = useLocation();
  
  // Safely get contexts - they may not be available if CustomTitleBar is rendered outside the providers
  const logoutPOSUser = useContext(POSUserContext)?.logoutPOSUser;
  const returnLockedRef = useRef(false);

  const showPinOnFirstScreen = useCallback(() => {
    try {
      if (logoutPOSUser) {
        void logoutPOSUser();
      }
    } catch (error) {
      console.error('[POS Logout debug] logoutPOSUser failed:', error);
    }
    window.dispatchEvent(new CustomEvent('pos-user-logout'));
    if (typeof Storage !== 'undefined' && window.localStorage) {
      Object.keys(window.localStorage)
        .filter((key) => key.startsWith('currentPOSUser'))
        .forEach((key) => window.localStorage.removeItem(key));
    }
    const hash = window.location.hash.replace(/^#/, '');
    const path = hash.split('?')[0];
    const onWorkstation = path === '/admin/setting' && hash.includes('section=workstation');
    if (!onWorkstation && path !== '/admin/pos') {
      window.location.hash = '#/admin/pos';
    }
  }, [logoutPOSUser]);

  useEffect(() => {
    setIsTauri(isTauriMode());
  }, []);

  useEffect(() => {
    const syncTopBar = () => setHideTopBar(readHideTopBar());
    window.addEventListener(WINDOW_CHROME_EVENT, syncTopBar);
    return () => window.removeEventListener(WINDOW_CHROME_EVENT, syncTopBar);
  }, []);

  useEffect(() => {
    if (!isTauriMode()) return undefined;
    let unlisten: (() => void) | undefined;
    let cancelled = false;
    (async () => {
      const { getCurrentWindow } = await import('@tauri-apps/api/window');
      const win = getCurrentWindow();
      const stop = await win.onFocusChanged(async ({ payload: focused }) => {
        if (!focused) {
          const minimized = await win.isMinimized().catch(() => false);
          const visible = await win.isVisible().catch(() => true);
          if (minimized || !visible) returnLockedRef.current = true;
          return;
        }
        if (!returnLockedRef.current) return;
        returnLockedRef.current = false;
        showPinOnFirstScreen();
      });
      if (cancelled) stop();
      else unlisten = stop;
    })();
    return () => {
      cancelled = true;
      unlisten?.();
    };
  }, [showPinOnFirstScreen]);

  useEffect(() => {
    if (!currentUser) {
      setShowAddressBar(false);
      return;
    }
    return onSnapshot(
      doc(db, 'systemConfig', 'posWindow'),
      (snap) => {
        setShowAddressBar(snap.exists() && snap.data()?.showAddressBar === true);
      },
      () => {
        setShowAddressBar(false);
      }
    );
  }, [currentUser]);

  useEffect(() => {
    if (!isTauri) return;
    let cancelled = false;
    (async () => {
      try {
        const { getCurrentWindow } = await import('@tauri-apps/api/window');
        const win = getCurrentWindow();
        if (cancelled || (await win.isMaximized()) || (await win.isFullscreen())) return;
        if (localStorage.getItem('pos_startInFullscreen') === 'true' || localStorage.getItem('pos_hideTaskbar') === 'true') return;
        await fitRestoredWindow();
      } catch {
        // Window API is not ready yet
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isTauri]);

  useEffect(() => {
    // Get app name from OEM branding (if loaded) or desktop API
    if (!oemLoading) {
      const brandName = getBrandName('en');
      setAppName(brandName);
    } else if (window.api?.appName) {
      window.api.appName().then(name => setAppName(name));
    }

    if (isTauri) {
      import('@tauri-apps/api/core').then(({ invoke }) => {
        invoke<string>('get_app_version').then(setAppVersion).catch(() => { });
      }).catch(() => { });
    } else if (window.api?.appVersion) {
      window.api.appVersion().then(version => setAppVersion(version));
    }
  }, [getBrandName, oemLoading, isTauri]);

  useEffect(() => {
    const checkMaximized = async () => {
      if (isTauri) {
        try {
          const { getCurrentWindow } = await import('@tauri-apps/api/window');
          const win = getCurrentWindow();
          setIsMaximized(await win.isMaximized());
        } catch {
          // Ignore until Tauri window API is ready
        }
      } else if (window.api?.windowIsMaximized) {
        const maximized = await window.api.windowIsMaximized();
        setIsMaximized(maximized);
      }
    };

    checkMaximized();
    const interval = setInterval(checkMaximized, 500);
    return () => clearInterval(interval);
  }, [isTauri]);

  const setPinnedMode = useCallback(async (on: boolean) => {
    if (!isTauri) return;
    try {
      const { getCurrentWindow } = await import('@tauri-apps/api/window');
      const win = getCurrentWindow();
      await win.setAlwaysOnTop(on);
      await win.setResizable(!on);
      await win.setMinimizable(!on);
      await win.setMaximizable(!on);
      await win.setClosable(!on);
      setIsPinned(on);
    } catch (e) {
      console.error('[CustomTitleBar] Failed to set pinned mode:', e);
    }
  }, [isTauri]);

  const handleMinimize = useCallback(async () => {
    if (isPinned) return;
    returnLockedRef.current = true;
    showPinOnFirstScreen();
    if (isTauri) {
      try {
        const { getCurrentWindow } = await import('@tauri-apps/api/window');
        await getCurrentWindow().minimize();
      } catch (e) {
        console.error('[CustomTitleBar] Tauri minimize failed:', e);
      }
    } else if (window.api?.windowMinimize) {
      window.api.windowMinimize();
    }
  }, [isTauri, isPinned, showPinOnFirstScreen]);

  const handleMaximize = useCallback(async () => {
    if (isPinned) return;
    if (isTauri) {
      try {
        const { getCurrentWindow } = await import('@tauri-apps/api/window');
        const win = getCurrentWindow();
        const maximized = await win.isMaximized();
        if (maximized) {
          await win.unmaximize();
          await fitRestoredWindow();
        } else {
          await win.maximize();
        }
        setIsMaximized(!maximized);
      } catch (e) {
        console.error('[CustomTitleBar] Tauri maximize failed:', e);
      }
    } else if (window.api?.windowMaximize) {
      window.api.windowMaximize();
    }
  }, [isTauri, isPinned]);


  useEffect(() => {
    // Update current URL when route changes
    const href = typeof window !== 'undefined' ? window.location.href : '';
    setCurrentUrl(href);
  }, [location]);

  return isFullscreen || hideTopBar ? null : (
    <div
      className="custom-title-bar fixed left-0 right-0 top-0 z-[9999] flex h-8 select-none items-center justify-between border-b border-gray-200 bg-white px-2 text-gray-700 dark:border-white/10 dark:bg-[#0c171d] dark:text-gray-100"
      data-tauri-drag-region={isPinned ? undefined : ''}
      style={{ WebkitAppRegion: isPinned ? 'no-drag' : 'drag' } as React.CSSProperties}
    >
      {/* App Icon and Title */}
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium">
          {appName}{appVersion ? ` v${appVersion}` : ''}
        </span>
        {isTauri && (
          <button
            type="button"
            aria-label={isPinned ? 'Unpin window' : 'Pin window'}
            onClick={() => setPinnedMode(!isPinned)}
            className={`flex h-5 w-5 items-center justify-center rounded-md border-none bg-transparent transition-all ${isPinned ? 'rotate-45 text-gray-600 opacity-90 dark:text-gray-200' : 'text-gray-400 opacity-50 hover:text-gray-600 hover:opacity-100 dark:hover:text-gray-100'}`}
            style={{ WebkitAppRegion: 'no-drag' } as React.CSSProperties}
          >
            <Pin className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {/* Current URL */}
      <div
        className="flex min-w-0 flex-1 items-center justify-center overflow-hidden px-2"
        data-tauri-drag-region={isPinned ? undefined : ''}
        style={{ WebkitAppRegion: isPinned ? 'no-drag' : 'drag' } as React.CSSProperties}
      >
        {showAddressBar && (
          <span className="max-w-full truncate font-mono text-xs text-gray-600 break-all dark:text-gray-300">
            {currentUrl || '—'}
          </span>
        )}
      </div>

      {/* Window Controls */}
      <div
        className={`flex gap-0 ${isPinned ? 'pointer-events-none cursor-not-allowed opacity-30' : ''}`}
        style={{ WebkitAppRegion: 'no-drag' } as React.CSSProperties}
      >
        <button
          type="button"
          aria-label="Minimize"
          onClick={handleMinimize}
          className="flex h-8 min-w-[46px] cursor-pointer items-center justify-center border-none bg-transparent text-gray-700 transition-colors hover:bg-gray-100 active:bg-gray-200 dark:text-gray-100 dark:hover:bg-white/10 dark:active:bg-white/15"
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
            <rect y="6" width="12" height="1" />
          </svg>
        </button>
        <button
          type="button"
          aria-label={isMaximized ? 'Restore' : 'Maximize'}
          onClick={handleMaximize}
          className="flex h-8 min-w-[46px] cursor-pointer items-center justify-center border-none bg-transparent text-gray-700 transition-colors hover:bg-gray-100 active:bg-gray-200 dark:text-gray-100 dark:hover:bg-white/10 dark:active:bg-white/15"
        >
          {isMaximized ? (
            <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
              <path d="M2 0v2H0v10h10V10h2V0H2zm1 1h7v7h-1V2H3V1zm-2 2h7v7H1V3z" />
            </svg>
          ) : (
            <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
              <rect width="12" height="12" fill="none" stroke="currentColor" strokeWidth="1" />
            </svg>
          )}
        </button>
      </div>
    </div>
  );
};

export default CustomTitleBar;
